/**
 * scheduleDraftReferenceDataService — ScheduleDraft 引用数据读取边界。
 *
 * 本模块封装飞机、机场、航段、机场约束和航路 by-leg 查询，并把 option 映射
 * 下沉到 lib/referenceDataMappers。它让引用数据 hooks 与表单组件不直接耦合
 * masterDataService 或 flight-routes API。
 *
 * 设计文档 → docs/10_core_contracts/12_api_v3_frontend_boundary.md
 * 骨架关联 → lib/referenceDataMappers.js (option mapper), hooks/useScheduleDraftFlightFormOptions.js (表单选项), components/ScheduleDraftFlightForm.jsx (航路选择)
 */

import { mapAircraftOption, mapAirportOption, mapFlightRouteOption } from "../lib/referenceDataMappers";
import { scheduleDraftReferenceApi } from "../../../../api/v3/scheduleDraftReferenceApi";
import { unwrapV3ListResponse, unwrapV3ResponseData } from "../../../../api/v3/responseContract";
import { getAircraftAvailabilityWindows } from "../../shared/aircraftAvailabilityApi";

const OPERATION_TRANSACTION_SUMMARY_BATCH_SIZE = 200;

async function getAirports() {
    const response = await scheduleDraftReferenceApi.getAirports({ page: 1, page_size: 500 });
    return unwrapV3ListResponse(response, "ScheduleDraft airports reference");
}

async function getAircrafts() {
    const response = await scheduleDraftReferenceApi.getAircrafts({ page: 1, page_size: 500, status: "ACTIVE" });
    return unwrapV3ListResponse(response, "ScheduleDraft aircraft reference");
}

async function getFlightLegs() {
    const response = await scheduleDraftReferenceApi.getFlightLegs({ page: 1, page_size: 500 });
    return unwrapV3ListResponse(response, "ScheduleDraft flight legs reference");
}

async function getAirportConstraints() {
    const response = await scheduleDraftReferenceApi.getAirportConstraints({ is_active: true });
    return unwrapV3ListResponse(response, "ScheduleDraft airport constraints reference");
}

function getRouteIdFromOperation(operation) {
    return operation?.flight_route_id
        || operation?.route_snapshot?.id
        || operation?.additional_info?.flight_route?.id
        || operation?.additionalInfo?.flightRoute?.id
        || null;
}

function normalizeAirportCode(value) {
    return String(value || "").trim().toUpperCase();
}

function getLegKey(departureAirport, arrivalAirport) {
    const dep = normalizeAirportCode(departureAirport);
    const arr = normalizeAirportCode(arrivalAirport);
    return dep && arr ? `${dep}:${arr}` : "";
}

function updateRouteUsageStats(stats, operation, index) {
    const routeId = getRouteIdFromOperation(operation);
    const legKey = getLegKey(operation?.departure_airport, operation?.arrival_airport);
    const targets = [];

    if (routeId) {
        targets.push([stats.routeStatsById, String(routeId)]);
    }
    if (legKey) {
        targets.push([stats.legStats, legKey]);
    }

    targets.forEach(([bucket, key]) => {
        const current = bucket[key] || {
            count: 0,
            recentIndex: index,
            recentFlightNumber: "",
            recentStcType: "",
        };
        bucket[key] = {
            ...current,
            count: current.count + 1,
            recentIndex: Math.min(current.recentIndex, index),
            recentFlightNumber: current.recentFlightNumber || operation?.flight_number || "",
            recentStcType: current.recentStcType || operation?.stc_type || "",
        };
    });
}

function getBlockTimeMinutes(value) {
    const minutes = value?.block_time_minutes ?? value?.min_block_time_minutes ?? null;
    return minutes == null ? null : Number(minutes);
}

function chunkArray(items, size) {
    const chunks = [];
    for (let index = 0; index < items.length; index += size) {
        chunks.push(items.slice(index, index + size));
    }
    return chunks;
}

async function getRouteById(routeId) {
    if (!routeId) {
        return null;
    }

    const response = await scheduleDraftReferenceApi.getFlightRouteById(routeId);
    return unwrapV3ResponseData(response) || null;
}

export const scheduleDraftReferenceDataService = {
    getAirports,

    getAircrafts,

    getAircraftAvailabilityWindows,

    getFlightLegs,

    getAirportConstraints,

    async getCreateDraftAircraftOptions() {
        const aircrafts = await getAircrafts();
        return aircrafts.map(mapAircraftOption);
    },

    async getFlightFormOptions() {
        const [airports, aircrafts] = await Promise.all([getAirports(), getAircrafts()]);

        return {
            airportOptions: airports.map(mapAirportOption),
            aircraftOptions: aircrafts.map(mapAircraftOption),
        };
    },

    async getActiveRoutes() {
        const response = await scheduleDraftReferenceApi.getFlightRoutes({ page: 1, page_size: 500, is_active: true });
        return unwrapV3ListResponse(response, "ScheduleDraft flight routes reference").filter((route) => route?.is_active !== false);
    },

    async getRoutesByLeg(departureAirport, arrivalAirport) {
        if (!departureAirport || !arrivalAirport) {
            return [];
        }

        const response = await scheduleDraftReferenceApi.getRoutesByLeg(departureAirport, arrivalAirport);
        return unwrapV3ListResponse(response, "ScheduleDraft flight routes by leg reference").map(mapFlightRouteOption);
    },

    async getBestMatchRoute(departureAirport, arrivalAirport, aircraftReg) {
        if (!departureAirport || !arrivalAirport) {
            return null;
        }

        const response = await scheduleDraftReferenceApi.getBestMatchRoute(departureAirport, arrivalAirport, aircraftReg ? { aircraft_reg: aircraftReg } : {});
        return unwrapV3ResponseData(response) || null;
    },

    async getRouteUsageStats(aircraftReg) {
        if (!aircraftReg) {
            return { routeStatsById: {}, legStats: {} };
        }

        const response = await scheduleDraftReferenceApi.getRecentOperations({
            page_size: 500,
            aircraft_reg: aircraftReg,
        });
        const stats = { routeStatsById: {}, legStats: {} };
        unwrapV3ListResponse(response, "ScheduleDraft recent operations reference").forEach((operation, index) => updateRouteUsageStats(stats, operation, index));
        return stats;
    },

    async getOperationTransactionSummaries(operationIds = []) {
        const ids = [...new Set(operationIds.map((value) => Number(value)).filter((value) => Number.isFinite(value)))];
        if (!ids.length) {
            return { items: [], total: 0 };
        }
        const chunkResults = await Promise.all(chunkArray(ids, OPERATION_TRANSACTION_SUMMARY_BATCH_SIZE).map(async (chunk) => {
            const response = await scheduleDraftReferenceApi.getOperationTransactionSummaries(chunk);
            return unwrapV3ListResponse(response, "ScheduleDraft operation transaction summaries");
        }));
        const items = chunkResults.flat();
        return { items, total: items.length };
    },

    getRouteById,

    async getV1Schedules(params = {}) {
        const response = await scheduleDraftReferenceApi.getV1Schedules({ page: 1, page_size: 100, ...params });
        return unwrapV3ListResponse(response, "ScheduleDraft V1 schedules reference");
    },

    async getRecentUsedRoute(departureAirport, arrivalAirport, aircraftReg) {
        if (!departureAirport || !arrivalAirport || !aircraftReg) {
            return null;
        }

        const response = await scheduleDraftReferenceApi.getRecentOperations({
            page_size: 1,
            aircraft_reg: aircraftReg,
            departure_airport: departureAirport,
            arrival_airport: arrivalAirport,
        });
        const operation = unwrapV3ListResponse(response, "ScheduleDraft recent operations reference")[0];
        const routeId = getRouteIdFromOperation(operation);
        if (routeId) {
            return getRouteById(routeId);
        }

        const operationBlockTime = getBlockTimeMinutes(operation);
        if (!operationBlockTime) {
            return null;
        }

        const routesResponse = await scheduleDraftReferenceApi.getRoutesByLeg(departureAirport, arrivalAirport, aircraftReg ? { aircraft_reg: aircraftReg } : {});
        const routes = unwrapV3ListResponse(routesResponse, "ScheduleDraft flight routes by leg reference");
        return routes.find((route) => getBlockTimeMinutes(route) === operationBlockTime) || null;
    },
};

export default scheduleDraftReferenceDataService;
